"use client";

import { ArrowUpRight } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const modules = [{
  number: "01",
  title: "Débouchage de Drain Principal",
  description: "Élimination complète des obstructions majeures causées par les racines, les graisses ou les débris accumulés dans votre conduite principale.",
  duration: "À partir de",
  format: "275$"
}, {
  number: "02",
  title: "Inspection par Caméra HD",
  description: "Diagnostic visuel précis de l'état de vos canalisations pour identifier les fissures, les affaissements ou les blocages récurrents.",
  duration: "Rapport",
  format: "Inclus"
}, {
  number: "03",
  title: "Nettoyage Haute Pression",
  description: "Restauration du débit optimal de vos drains grâce à une technologie de jet d'eau pulsé, sans produits chimiques corrosifs.",
  duration: "Efficacité",
  format: "100%"
}, {
  number: "04",
  title: "Localisation de Conduites",
  description: "Repérage exact du tracé de vos drains souterrains avant des travaux d'excavation ou pour trouver un point de rupture précis.",
  duration: "Précision",
  format: "Chirurgicale"
}, {
  number: "05",
  title: "Prévention des Refoulements",
  description: "Analyse des risques et recommandations techniques pour protéger votre sous-sol contre les inondations et les retours d'égout.",
  duration: "Service",
  format: "Expert"
}, {
  number: "06",
  title: "Urgence 24/7 Grand Montréal",
  description: "Intervention rapide à Montréal, Laval et Longueuil pour stabiliser vos systèmes de drainage et prévenir les dommages matériels.",
  duration: "Réponse",
  format: "Immédiate"
}];
function ModuleRow({
  module,
  i
}: {
  module: (typeof modules)[number];
  i: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} data-index={i} className={`group relative py-10 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
    transitionDelay: `${i * 80}ms`
  }}>
      <div className="grid grid-cols-12 gap-6 md:gap-10 items-start">
        {}
        <div className="col-span-2 md:col-span-1">
          <span className="font-serif italic text-5xl md:text-6xl font-light text-primary inline-block transition-all duration-500 ease-out group-hover:translate-x-2 group-hover:scale-110 origin-left">
            {module.number}
          </span>
        </div>

        {}
        <div className="col-span-10 md:col-span-8">
          <h3 className="font-default text-2xl md:text-3xl lg:text-4xl font-semibold leading-tight text-foreground transition-colors duration-500 group-hover:text-primary">
            {module.title}
          </h3>
          <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground mt-3 max-w-2xl">
            {module.description}
          </p>
        </div>

        {}
        <div className="col-span-12 md:col-span-3 flex md:flex-col md:items-end gap-8 md:gap-4 mt-2 md:mt-3">
          <div className="flex flex-col md:items-end">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {module.duration === "À partir de" ? "Tarif" : "Type"}
            </span>
            <span className="font-default text-sm font-medium text-foreground mt-1">
              {module.duration}
            </span>
          </div>
          <div className="flex flex-col md:items-end">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {module.format.includes("$") ? "Base" : "Statut"}
            </span>
            <span className="font-default text-sm font-bold text-secondary mt-1">
              {module.format}
            </span>
          </div>
          <div className="hidden md:flex items-center justify-end mt-2 opacity-0 -translate-x-2 transition-all duration-500 group-hover:opacity-100 group-hover:translate-x-0">
            <ArrowUpRight className="w-5 h-5 text-primary" strokeWidth={1.5} />
          </div>
        </div>
      </div>
    </div>;
}
export default function HeuresDOuverture() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: footerRef,
    isVisible: footerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="4019" id="heures-douverture" className="bg-background text-foreground py-24 md:py-40 px-6 md:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`grid grid-cols-12 gap-8 md:gap-12 mb-20 md:mb-28 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="col-span-12 md:col-span-5">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-primary mb-8">
              [02] — Expertise Technique
            </p>
            <h2 className="font-default text-5xl md:text-6xl lg:text-7xl font-semibold leading-[1.05] text-foreground">
              Nos solutions de
              <br />
              <span className="italic text-primary">drainage.</span>
            </h2>
          </div>

          <div className="col-span-12 md:col-span-6 md:col-start-7 md:pt-16">
            <p className="font-default text-lg md:text-xl leading-relaxed text-muted-foreground">
              OT Solution se spécialise exclusivement dans le débouchage et l'inspection par caméra. Notre approche méthodique garantit une intervention propre, rapide et durable pour les propriétaires résidentiels et commerciaux du Grand Montréal.
            </p>
          </div>
        </div>

        {}
        <div className="max-w-5xl mx-auto">
          <Separator className="bg-border/40" />
          {modules.map((module, i) => <div key={module.number}>
              <ModuleRow module={module} i={i} />
              <Separator className="bg-border/40" />
            </div>)}

          {}
          <div ref={footerRef} className={`pt-12 md:pt-16 text-center transition-all duration-1000 ease-out ${footerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <p className="font-default text-base md:text-lg text-muted-foreground">
              Disponibilité immédiate pour urgences.{" "}
              <span className="text-foreground font-semibold">Appelez le 438-367-8309.</span>
            </p>
          </div>
        </div>
      </div>
    </section>;
}